/**
 * Menu query for one date (replacement data layer).
 */
import { useEffect, useRef, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../../shared/auth/store';
import { useSubscription } from '../subscription/hooks';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';
import type { MenuBuilder } from './types';
import { policies } from '../../query/policies';
import {
  isPastMenuDate,
  PAST_DATE_MENU_STALE_MS,
} from './cache';
import { fetchMenu, fetchGuestMenuPreview } from './api';
import { menuKeys } from './keys';
import { isBootMenuPrefetchComplete, subscribeBootMenuPrefetchComplete } from '../menuPrefetch';

export type MenuForDateQueryOptions = {
  guest_menu_preview?: boolean;
  /** Guest browse: ordered selectable dates — enables one-day-ahead prefetch only. */
  guest_selectable_dates?: string[];
  /** When false, the React Query observer stays disabled (used by active-menu bridge). */
  query_enabled?: boolean;
};

function prefetchDate(
  query_client: ReturnType<typeof useQueryClient>,
  customer_id: number,
  date: string,
  getDayConfig: (d: string) => { isDoubleBox?: boolean } | null,
): Promise<void> {
  const date_ascii = ensureAsciiDateString(date) || date;
  const key_box1 = menuKeys.day(customer_id, date_ascii, false);
  const key_box2 = menuKeys.day(customer_id, date_ascii, true);
  const is_double = getDayConfig(date_ascii)?.isDoubleBox === true;
  const is_past = isPastMenuDate(date_ascii);
  const stale = is_past ? PAST_DATE_MENU_STALE_MS : policies.menu.staleTime;
  const gc = is_past ? PAST_DATE_MENU_STALE_MS : policies.menu.gcTime;
  const promises: Promise<unknown>[] = [];

  if (query_client.getQueryData(key_box1) == null) {
    promises.push(
      query_client.prefetchQuery({
        queryKey: key_box1,
        queryFn: () => fetchMenu(customer_id, date_ascii, false),
        staleTime: stale,
        gcTime: gc,
      }),
    );
  } else if (is_past) {
    return Promise.resolve();
  }
  if (is_double && query_client.getQueryData(key_box2) == null) {
    promises.push(
      query_client.prefetchQuery({
        queryKey: key_box2,
        queryFn: () => fetchMenu(customer_id, date_ascii, true),
        staleTime: stale,
        gcTime: gc,
      }),
    );
  }
  return Promise.all(promises).then(() => {});
}

export function useMenuDate(date: string, isDoubleBox?: boolean, options?: MenuForDateQueryOptions) {
  const query_client = useQueryClient();
  const customer_id = useAuthStore(s => s.user?.id);
  const { availableDates, getDayConfig } = useSubscription();
  const date_ascii = ensureAsciiDateString(date) || date;
  const latest_date_ref = useRef(date_ascii);
  latest_date_ref.current = date_ascii;
  const [boot_prefetch_done, set_boot_prefetch_done] = useState(isBootMenuPrefetchComplete);

  useEffect(() => subscribeBootMenuPrefetchComplete(() => set_boot_prefetch_done(true)), []);

  /** Fallback when boot prefetch never claims (e.g. empty dates then late fill without provider). */
  useEffect(() => {
    if (boot_prefetch_done || availableDates.length === 0) {
      return;
    }
    const timer = setTimeout(() => set_boot_prefetch_done(true), 2500);
    return () => clearTimeout(timer);
  }, [boot_prefetch_done, availableDates.length]);

  const guest_preview = options?.guest_menu_preview === true;
  const enabled =
    options?.query_enabled !== false &&
    date_ascii.length >= 10 &&
    (guest_preview || typeof customer_id === 'number');
  const is_double = Boolean(isDoubleBox);
  const is_past = !guest_preview && isPastMenuDate(date_ascii);
  const menu_key = guest_preview
    ? menuKeys.guestPreview(date_ascii)
    : menuKeys.day(customer_id!, date_ascii, is_double);

  const query = useQuery({
    queryKey: menu_key,
    queryFn: () =>
      guest_preview ? fetchGuestMenuPreview(date_ascii) : fetchMenu(customer_id!, date_ascii, is_double),
    enabled,
    staleTime: is_past ? PAST_DATE_MENU_STALE_MS : policies.menu.staleTime,
    gcTime: is_past ? PAST_DATE_MENU_STALE_MS : policies.menu.gcTime,
    refetchOnMount: !is_past,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (options?.query_enabled === false) {
      return;
    }
    if (!boot_prefetch_done) {
      return;
    }
    if (guest_preview) {
      const selectable = options?.guest_selectable_dates ?? [];
      if (!date_ascii || date_ascii.length < 10 || selectable.length === 0) {
        return;
      }
      const idx = selectable.indexOf(date_ascii);
      if (idx < 0 || idx >= selectable.length - 1) {
        return;
      }
      const next_date = selectable[idx + 1]!;
      const key_next = menuKeys.guestPreview(next_date);
      if (query_client.getQueryData(key_next) != null) {
        return;
      }
      query_client.prefetchQuery({
        queryKey: key_next,
        queryFn: () => fetchGuestMenuPreview(next_date),
        staleTime: policies.menu.staleTime,
        gcTime: policies.menu.gcTime,
      });
      return;
    }
    if (
      typeof customer_id !== 'number' ||
      availableDates.length === 0 ||
      !date_ascii ||
      date_ascii.length < 10
    ) {
      return;
    }
    const sorted = [...availableDates].map(d => ensureAsciiDateString(d) || d).sort();
    const preloadNext = () => {
      const current = latest_date_ref.current;
      const idx = sorted.indexOf(current);
      if (idx < 0 || idx >= sorted.length - 1) {
        return;
      }
      const next_date = sorted[idx + 1]!;
      const key_next = menuKeys.day(customer_id, next_date, false);
      const has_next = query_client.getQueryData(key_next) != null;

      if (!has_next) {
        prefetchDate(query_client, customer_id, next_date, getDayConfig).then(preloadNext);
        return;
      }
      if (idx >= sorted.length - 2) {
        return;
      }
      const next_next_date = sorted[idx + 2]!;
      const key_next_next = menuKeys.day(customer_id, next_next_date, false);
      if (query_client.getQueryData(key_next_next) != null) {
        return;
      }
      prefetchDate(query_client, customer_id, next_next_date, getDayConfig).then(preloadNext);
    };
    const timer = setTimeout(preloadNext, 0);
    return () => clearTimeout(timer);
  }, [
    guest_preview,
    customer_id,
    date_ascii,
    availableDates,
    query_client,
    getDayConfig,
    options?.query_enabled,
    options?.guest_selectable_dates,
    boot_prefetch_done,
  ]);

  const menu_builders = (query.data as { menu_builders?: MenuBuilder[] } | undefined)?.menu_builders;
  const data = useMemo(
    () => (menu_builders != null ? { menuBuilders: menu_builders ?? [] } : undefined),
    [menu_builders],
  );

  return {
    data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
    error: query.error,
  };
}

export { useMenuDate as useMenuForDateQuery };
export const MENU_CACHE_STALE_MS = policies.menu.staleTime;
export const MENU_CACHE_GC_MS = policies.menu.gcTime;
